import React, { useState, useEffect } from "react";
import {
  Plus,
  Trash2,
  Eye,
  X,
  Check,
  AlertCircle,
  RefreshCw,
  ToggleLeft,
  ToggleRight,
  Music,
  Folder,
  Loader,
  Search,
  ChevronLeft,
  ChevronRight,
  Upload,
} from "lucide-react";

import { useTheme } from "../context/ThemeContext";

const API_BASE_URL = process.env.REACT_APP_BASE_URL;

const getAuthToken = () => {
  return localStorage.getItem("accessToken");
};

const MusicManagement = () => {
  const { isDarkMode } = useTheme();

  const [activeTab, setActiveTab] = useState("categories");
  const [categories, setCategories] = useState([]);
  const [songs, setSongs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [selectedSongFile, setSelectedSongFile] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: 10,
    totalPages: 1,
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");

  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showSongModal, setShowSongModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [formSubmitting, setFormSubmitting] = useState(false);

  const [confirmModal, setConfirmModal] = useState({
    show: false,
    title: "",
    message: "",
    onConfirm: null,
    itemId: null,
  });

  const [categoryForm, setCategoryForm] = useState({ categoryName: "" });
  const [songForm, setSongForm] = useState({
    songName: "",
    categoryId: "",
    songUrl: "",
  });

  const [uploadingFiles, setUploadingFiles] = useState({
    song: false,
  });

  // API Helper
  const apiCall = async (endpoint, options = {}) => {
    try {
      if (!API_BASE_URL) {
        throw new Error("REACT_APP_BASE_URL is not configured in .env");
      }

      const authToken = getAuthToken();
      const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        ...options,
        headers: {
          "Content-Type": "application/json",
          ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
          ...options.headers,
        },
      });

      const responseText = await response.text();
      let data = null;

      if (responseText) {
        try {
          data = JSON.parse(responseText);
        } catch {
          if (
            responseText.trim().startsWith("<!DOCTYPE") ||
            responseText.trim().startsWith("<html")
          ) {
            throw new Error(
              "API returned HTML instead of JSON. Check REACT_APP_BASE_URL and backend availability.",
            );
          }
          throw new Error("Invalid JSON response from API");
        }
      }

      if (!response.ok) {
        throw new Error(
          data?.message || `API request failed (${response.status})`,
        );
      }

      return data;
    } catch (err) {
      throw new Error(err.message || "Network error");
    }
  };

  // Fetch Categories
  const fetchCategories = async () => {
    try {
      setLoading(true);
      const data = await apiCall("/music/categories");
      setCategories(data.data || []);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Fetch Songs
  const fetchSongs = async () => {
    try {
      setLoading(true);
      const queryParams = new URLSearchParams({
        page: currentPage.toString(),
        limit: pagination.limit.toString(),
        ...(searchTerm && { search: searchTerm }),
        ...(selectedCategory && { categoryId: selectedCategory }),
      });

      const data = await apiCall(`/music/songs?${queryParams}`);
      setSongs(data.data || []);
      setPagination({
        total: data.total || 0,
        page: data.page || 1,
        limit: data.limit || 10,
        totalPages: Math.ceil((data.total || 0) / (data.limit || 10)),
      });
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Upload file to S3 using presigned URL
  const uploadToS3 = async (file, type) => {
    try {
      const presignedData = await apiCall("/uploads/presigned-url", {
        method: "POST",
        body: JSON.stringify({
          fileName: file.name,
          type: "music",
          subfolder: "music",
        }),
      });

      const { uploadUrl, key } = presignedData.data;

      const uploadResponse = await fetch(uploadUrl, {
        method: "PUT",
        body: file,
        headers: {
          "Content-Type": file.type,
        },
      });

      if (!uploadResponse.ok) {
        throw new Error("Failed to upload file to S3");
      }

      const publicUrl = uploadUrl.split("?")[0];
      return { key, publicUrl };
    } catch (err) {
      throw new Error(`Upload failed: ${err.message}`);
    }
  };

  // Create Category
  const handleCreateCategory = async (e) => {
    e.preventDefault();
    const trimmedName = (categoryForm.categoryName || "").trim();

    if (!trimmedName) {
      setError("Category name is required");
      return;
    }

    try {
      setFormSubmitting(true);
      await apiCall("/music/category", {
        method: "POST",
        body: JSON.stringify({
          categoryName: trimmedName,
        }),
      });

      setSuccess("Category created successfully!");
      setShowCategoryModal(false);
      setCategoryForm({ categoryName: "" });
      fetchCategories();
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setFormSubmitting(false);
    }
  };

  // Toggle Category
  const [togglingCategoryId, setTogglingCategoryId] = useState(null);
  const handleToggleCategory = async (id) => {
    setTogglingCategoryId(id);
    try {
      await apiCall(`/music/category/toggle/${id}`, {
        method: "POST",
      });
      setSuccess("Category status updated!");
      fetchCategories();
      fetchSongs();
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setTogglingCategoryId(null);
    }
  };

  // Delete Category
  const handleDeleteCategory = (id) => {
    setConfirmModal({
      show: true,
      title: "Delete Category",
      message:
        "Are you sure you want to delete this category? This will soft-delete the category and all its songs.",
      onConfirm: async () => {
        try {
          await apiCall(`/music/category/${id}`, {
            method: "DELETE",
          });
          setSuccess("Category deleted successfully!");
          setConfirmModal({
            show: false,
            title: "",
            message: "",
            onConfirm: null,
            itemId: null,
          });
          fetchCategories();
          setError(null);
        } catch (err) {
          setError(err.message);
        }
      },
      itemId: id,
    });
  };

  // Handle file selection for songs - uploads immediately
  const handleSongFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setSelectedSongFile(file);
    setSongForm({ ...songForm, songUrl: "" });
  };

  // Re-upload file (change uploaded file)
  const handleReuploadFile = () => {
    document.getElementById("song-file-input")?.click();
  };

  // Clear file upload
  const handleClearFile = () => {
    setSongForm({
      ...songForm,
      songUrl: "",
    });
    const input = document.getElementById("song-file-input");
    if (input) input.value = "";
  };

  // Create Song
  const handleCreateSong = async (e) => {
    e.preventDefault();

    const songName = songForm.songName.trim();
    const categoryId = songForm.categoryId.trim();

    if (!songName || !categoryId || !selectedSongFile) {
      setError("All fields including file are required");
      return;
    }

    try {
      setFormSubmitting(true);

      // Upload only when user clicks ADD
      const { publicUrl } = await uploadToS3(selectedSongFile, "song");

      // Set URL to form
      const songUrl = publicUrl;

      // Save song to backend
      await apiCall("/music/song", {
        method: "POST",
        body: JSON.stringify({
          songName,
          categoryId,
          songUrl,
        }),
      });

      setSuccess("Song created successfully!");
      setShowSongModal(false);
      setSongForm({ songName: "", categoryId: "", songUrl: "" });
      setSelectedSongFile(null);

      fetchSongs();
    } catch (err) {
      setError(err.message);
    } finally {
      setFormSubmitting(false);
    }
  };

  // Delete Song
  const handleDeleteSong = (id) => {
    setConfirmModal({
      show: true,
      title: "Delete Song",
      message:
        "Are you sure you want to delete this song? This action cannot be undone.",
      onConfirm: async () => {
        try {
          await apiCall(`/music/song/${id}`, {
            method: "DELETE",
          });
          setSuccess("Song deleted successfully!");
          setConfirmModal({
            show: false,
            title: "",
            message: "",
            onConfirm: null,
            itemId: null,
          });
          fetchSongs();
          setError(null);
        } catch (err) {
          setError(err.message);
        }
      },
      itemId: id,
    });
  };

  // Load data on mount and tab change
  useEffect(() => {
    if (activeTab === "categories") {
      fetchCategories();
    } else {
      fetchSongs();
    }
  }, [activeTab, currentPage, searchTerm, selectedCategory]);

  // Clear messages
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  const visibleSongs = songs.filter(
    (song) =>
      song?.categoryId?.isDeleted !== true && song?.categoryId?.isActive !== false,
  );

  if (loading && categories.length === 0 && songs.length === 0) {
    return (
      <div
        className={`flex flex-col min-h-screen ${
          isDarkMode ? "bg-black" : "bg-gray-100"
        }`}
      >
        <div
          className={`flex-grow flex items-center justify-center ${
            isDarkMode ? "text-white" : "text-gray-900"
          }`}
        >
          <div className="text-center">
            <Loader className="animate-spin w-12 h-12 mx-auto mb-4 text-orange-400" />
            <p className="text-xl">Loading music data...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`min-h-screen ${
        isDarkMode ? "bg-black text-white" : "bg-gray-100 text-gray-900"
      }`}
    >
      <div className="max-w-7xl mx-auto p-6 space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-orange-400 rounded-full flex items-center justify-center">
              <Music className="text-black" size={24} />
            </div>
            <div>
              <h1
                className={`text-3xl font-bold ${
                  isDarkMode ? "text-white" : "text-gray-900"
                }`}
              >
                Music Management
              </h1>
              <p
                className={
                  isDarkMode ? "text-gray-400 mt-1" : "text-gray-600 mt-1"
                }
              >
                Manage music categories and songs
              </p>
            </div>
          </div>
        </div>

        {error && (
          <div
            className={`border rounded-xl p-4 flex items-start gap-3 animate-in fade-in ${
              isDarkMode
                ? "bg-red-500/10 border-red-500/30 text-red-400"
                : "bg-red-50 border-red-200 text-red-700"
            }`}
          >
            <AlertCircle size={20} className="flex-shrink-0 mt-0.5" />
            <div className="flex-1">{error}</div>
            <button
              onClick={() => setError(null)}
              className="flex-shrink-0 hover:opacity-70"
            >
              <X size={18} />
            </button>
          </div>
        )}

        {success && (
          <div
            className={`border rounded-xl p-4 flex items-start gap-3 animate-in fade-in ${
              isDarkMode
                ? "bg-green-500/10 border-green-500/30 text-green-400"
                : "bg-green-50 border-green-200 text-green-700"
            }`}
          >
            <Check size={20} className="flex-shrink-0 mt-0.5" />
            <div className="flex-1">{success}</div>
            <button
              onClick={() => setSuccess(null)}
              className="flex-shrink-0 hover:opacity-70"
            >
              <X size={18} />
            </button>
          </div>
        )}

        <div
          className={`flex gap-4 border-b ${
            isDarkMode ? "border-zinc-800" : "border-gray-200"
          }`}
        >
          <button
            onClick={() => setActiveTab("categories")}
            className={`px-6 py-3 font-medium border-b-2 transition-colors flex items-center gap-2 ${
              activeTab === "categories"
                ? isDarkMode
                  ? "border-orange-400 text-orange-400"
                  : "border-orange-500 text-orange-600"
                : isDarkMode
                  ? "border-transparent text-gray-400 hover:text-white"
                  : "border-transparent text-gray-600 hover:text-gray-900"
            }`}
          >
            <Folder size={20} />
            Categories
          </button>
          <button
            onClick={() => setActiveTab("songs")}
            className={`px-6 py-3 font-medium border-b-2 transition-colors flex items-center gap-2 ${
              activeTab === "songs"
                ? isDarkMode
                  ? "border-orange-400 text-orange-400"
                  : "border-orange-500 text-orange-600"
                : isDarkMode
                  ? "border-transparent text-gray-400 hover:text-white"
                  : "border-transparent text-gray-600 hover:text-gray-900"
            }`}
          >
            <Music size={20} />
            Songs
          </button>
        </div>

        {activeTab === "categories" && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <button
                onClick={fetchCategories}
                className={`flex items-center gap-2 px-4 py-2 font-semibold transition-colors ${
                  isDarkMode
                    ? "text-orange-400 hover:text-orange-300"
                    : "text-orange-600 hover:text-orange-700"
                }`}
                disabled={loading}
              >
                <RefreshCw
                  size={18}
                  className={loading ? "animate-spin" : ""}
                />
                Refresh
              </button>
              <button
                onClick={() => {
                  setCategoryForm({ categoryName: "" });
                  setShowCategoryModal(true);
                }}
                className="flex items-center gap-2 px-6 py-3 bg-orange-400 text-black font-semibold rounded-lg hover:bg-orange-500 transition-colors shadow-lg"
              >
                <Plus size={20} />
                Add Category
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {categories.filter((cat) => !cat.isDeleted).length === 0 ? (
                <div
                  className={`col-span-full ${
                    isDarkMode
                      ? "bg-black border-zinc-800"
                      : "bg-white border-gray-200"
                  } border rounded-xl p-12 text-center`}
                >
                  <Folder
                    size={48}
                    className={`mx-auto mb-4 ${
                      isDarkMode ? "text-gray-400" : "text-gray-600"
                    }`}
                  />
                  <p className={isDarkMode ? "text-gray-400" : "text-gray-600"}>
                    No categories found
                  </p>
                </div>
              ) : (
                categories
                  .filter((cat) => !cat.isDeleted)
                  .map((category) => (
                    <div
                      key={category._id}
                      className={`${
                        isDarkMode
                          ? "bg-black border-zinc-800"
                          : "bg-white border-gray-200"
                      } border rounded-xl p-5 transition-all hover:shadow-lg`}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="w-10 h-10 bg-orange-400 rounded-lg flex items-center justify-center">
                            <Folder size={20} className="text-black" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <h3
                              className={`font-semibold text-lg ${
                                isDarkMode ? "text-white" : "text-gray-900"
                              } truncate`}
                              title={category.categoryName || "Untitled"}
                            >
                              {category.categoryName || "Untitled"}
                            </h3>
                            <p
                              className={`text-xs ${
                                isDarkMode ? "text-gray-400" : "text-gray-600"
                              }`}
                            >
                              {category.isActive ? "Active" : "Inactive"}
                            </p>
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-2 mt-4">
                        <button
                          onClick={() => handleToggleCategory(category._id)}
                          disabled={togglingCategoryId === category._id}
                          className={`flex-1 h-11 px-3 rounded-lg transition-all flex items-center justify-center gap-2 border ${
                            togglingCategoryId === category._id
                              ? "opacity-60 cursor-not-allowed"
                              : ""
                          } ${
                            isDarkMode
                              ? "border-zinc-700 hover:bg-zinc-800"
                              : "border-gray-300 hover:bg-gray-100"
                          }`}
                          title={category.isActive ? "Deactivate" : "Activate"}
                        >
                          {togglingCategoryId === category._id ? (
                            <>
                              <Loader size={20} className={`animate-spin ${isDarkMode ? "text-gray-300" : "text-gray-500"}`} />
                              <span className={`text-sm font-semibold ${isDarkMode ? "text-gray-300" : "text-gray-500"}`}>
                                Updating...
                              </span>
                            </>
                          ) : category.isActive ? (
                            <>
                              <ToggleRight size={28} className="text-green-400" />
                              <span className="text-sm font-semibold text-green-400">
                                Active
                              </span>
                            </>
                          ) : (
                            <>
                              <ToggleLeft
                                size={28}
                                className={`${
                                  isDarkMode ? "text-gray-400" : "text-gray-600"
                                }`}
                              />
                              <span
                                className={`text-sm font-semibold ${
                                  isDarkMode ? "text-gray-400" : "text-gray-600"
                                }`}
                              >
                                Inactive
                              </span>
                            </>
                          )}
                        </button>
                        <button
                          onClick={() => handleDeleteCategory(category._id)}
                          className={`flex-1 p-2 rounded transition-colors ${
                            isDarkMode
                              ? "hover:bg-zinc-800"
                              : "hover:bg-gray-100"
                          }`}
                          title="Delete"
                        >
                          <Trash2 size={20} className="text-red-400 mx-auto" />
                        </button>
                      </div>
                    </div>
                  ))
              )}
            </div>

            {showCategoryModal && (
              <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                <div
                  className={`${
                    isDarkMode
                      ? "bg-black border-zinc-800"
                      : "bg-white border-gray-200"
                  } border rounded-2xl max-w-md w-full p-6`}
                >
                  <div className="flex items-center justify-between mb-6">
                    <h2
                      className={`text-2xl font-bold ${
                        isDarkMode ? "text-white" : "text-gray-900"
                      }`}
                    >
                      Add Category
                    </h2>
                    <button
                      onClick={() => setShowCategoryModal(false)}
                      className={`${
                        isDarkMode
                          ? "text-gray-400 hover:text-white"
                          : "text-gray-600 hover:text-gray-900"
                      } transition-colors`}
                    >
                      <X size={24} />
                    </button>
                  </div>

                  <form onSubmit={handleCreateCategory} className="space-y-4">
                    <div>
                      <label
                        className={`block text-sm font-medium mb-2 ${
                          isDarkMode ? "text-gray-400" : "text-gray-600"
                        }`}
                      >
                        Category Name *
                      </label>
                      <input
                        type="text"
                        value={categoryForm.categoryName || ""}
                        onChange={(e) =>
                          setCategoryForm({
                            categoryName: e.target.value.slice(0, 50),
                          })
                        }
                        placeholder="Enter category name"
                        maxLength={50}
                        className={`w-full px-4 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:ring-orange-400 ${
                          isDarkMode
                            ? "bg-zinc-800 border-zinc-700 text-white"
                            : "bg-gray-100 border-gray-300 text-gray-900"
                        }`}
                      />
                      <p
                        className={`text-xs mt-1 text-right ${
                          isDarkMode ? "text-gray-500" : "text-gray-500"
                        }`}
                      >
                        {(categoryForm.categoryName || "").length}/50
                      </p>
                    </div>

                    <div className="flex gap-3 pt-4">
                      <button
                        type="button"
                        onClick={() => setShowCategoryModal(false)}
                        disabled={formSubmitting}
                        className={`flex-1 px-4 py-2.5 rounded-lg font-medium border transition-colors disabled:opacity-50 ${
                          isDarkMode
                            ? "border-zinc-700 text-white hover:bg-zinc-800"
                            : "border-gray-300 text-gray-900 hover:bg-gray-100"
                        }`}
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        disabled={
                          formSubmitting ||
                          !(categoryForm.categoryName || "").trim()
                        }
                        className="flex-1 px-4 py-2.5 bg-orange-400 text-black font-semibold rounded-lg hover:bg-orange-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                      >
                        {formSubmitting ? (
                          <>
                            <RefreshCw size={16} className="animate-spin" />
                            Adding...
                          </>
                        ) : (
                          <>
                            <Plus size={16} />
                            Add Category
                          </>
                        )}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === "songs" && (
          <div className="space-y-6">
            <div
              className={`${
                isDarkMode
                  ? "bg-black border-zinc-800"
                  : "bg-white border-gray-200"
              } border rounded-xl p-5`}
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDarkMode ? "text-gray-400" : "text-gray-600"
                    }`}
                  >
                    Search Songs
                  </label>
                  <div className="relative">
                    <Search
                      size={18}
                      className={`absolute left-3 top-3 ${
                        isDarkMode ? "text-gray-400" : "text-gray-600"
                      }`}
                    />
                    <input
                      type="text"
                      value={searchTerm}
                      onChange={(e) => {
                        setSearchTerm(e.target.value.slice(0, 100));
                        setCurrentPage(1);
                      }}
                      placeholder="Search by song name..."
                      maxLength={100}
                      className={`w-full pl-10 pr-4 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:ring-orange-400 ${
                        isDarkMode
                          ? "bg-zinc-800 border-zinc-700 text-white"
                          : "bg-gray-100 border-gray-300 text-gray-900"
                      }`}
                    />
                  </div>
                </div>

                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDarkMode ? "text-gray-400" : "text-gray-600"
                    }`}
                  >
                    Filter by Category
                  </label>
                  <select
                    value={selectedCategory}
                    onChange={(e) => {
                      setSelectedCategory(e.target.value);
                      setCurrentPage(1);
                    }}
                    className={`w-full px-4 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:ring-orange-400 ${
                      isDarkMode
                        ? "bg-zinc-800 border-zinc-700 text-white"
                        : "bg-gray-100 border-gray-300 text-gray-900"
                    }`}
                  >
                    <option value="">All Categories</option>
                    {categories
                      .filter((c) => c.isActive && !c.isDeleted)
                      .map((cat) => (
                        <option key={cat._id} value={cat._id}>
                          {cat.categoryName || "Untitled"}
                        </option>
                      ))}
                  </select>
                </div>
              </div>
            </div>

            <div className="flex justify-end">
              <button
                onClick={() => {
                  setSongForm({
                    songName: "",
                    categoryId: "",
                    songUrl: "",
                  });
                  setShowSongModal(true);
                }}
                className="flex items-center gap-2 px-6 py-3 bg-orange-400 text-black font-semibold rounded-lg hover:bg-orange-500 transition-colors shadow-lg"
              >
                <Plus size={20} />
                Add Song
              </button>
            </div>

            <div className="grid grid-cols-1 gap-4">
              {visibleSongs.length === 0 ? (
                <div
                  className={`${
                    isDarkMode
                      ? "bg-black border-zinc-800"
                      : "bg-white border-gray-200"
                  } border rounded-xl p-12 text-center`}
                >
                  <Music
                    size={48}
                    className={`mx-auto mb-4 ${
                      isDarkMode ? "text-gray-400" : "text-gray-600"
                    }`}
                  />
                  <p className={isDarkMode ? "text-gray-400" : "text-gray-600"}>
                    No songs found
                  </p>
                </div>
              ) : (
                visibleSongs.map((song) => (
                  <div
                    key={song._id}
                    className={`${
                      isDarkMode
                        ? "bg-black border-zinc-800 hover:bg-zinc-900"
                        : "bg-white border-gray-200 hover:bg-gray-50"
                    } border rounded-xl p-5 flex items-center justify-between transition-colors`}
                  >
                    <div className="flex items-center gap-4 flex-1">
                      <div className="w-16 h-16 rounded-lg bg-orange-400 flex items-center justify-center">
                        <Music size={32} className="text-black" />
                      </div>
                      <div>
                        <h3
                          className={`font-semibold text-lg ${
                            isDarkMode ? "text-white" : "text-gray-900"
                          }`}
                        >
                          {song.songName || "Untitled"}
                        </h3>
                        <p
                          className={`text-sm ${
                            isDarkMode ? "text-gray-400" : "text-gray-600"
                          }`}
                        >
                          {song.categoryId?.categoryName || "N/A"}
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => {
                          setSelectedItem(song);
                          setShowViewModal(true);
                        }}
                        className={`p-2 rounded transition-colors ${
                          isDarkMode ? "hover:bg-zinc-800" : "hover:bg-gray-100"
                        }`}
                        title="View"
                      >
                        <Eye size={20} className="text-blue-400" />
                      </button>
                      <button
                        onClick={() => handleDeleteSong(song._id)}
                        className={`p-2 rounded transition-colors ${
                          isDarkMode ? "hover:bg-zinc-800" : "hover:bg-gray-100"
                        }`}
                        title="Delete"
                      >
                        <Trash2 size={20} className="text-red-400" />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>

            {visibleSongs.length > 0 && (
              <div
                className={`flex items-center justify-center gap-4 mt-5 pt-5 border-t ${
                  isDarkMode ? "border-zinc-800" : "border-gray-200"
                }`}
              >
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className={`transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                    isDarkMode
                      ? "text-gray-400 hover:text-white"
                      : "text-gray-600 hover:text-gray-900"
                  }`}
                >
                  <ChevronLeft size={20} />
                </button>
                <span
                  className={`text-sm ${
                    isDarkMode ? "text-gray-400" : "text-gray-600"
                  }`}
                >
                  Page {pagination.page} of {pagination.totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage(currentPage + 1)}
                  disabled={currentPage >= pagination.totalPages}
                  className={`transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                    isDarkMode
                      ? "text-gray-400 hover:text-white"
                      : "text-gray-600 hover:text-gray-900"
                  }`}
                >
                  <ChevronRight size={20} />
                </button>
              </div>
            )}
          </div>
        )}

        {showSongModal && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div
              className={`${
                isDarkMode
                  ? "bg-black border-zinc-800"
                  : "bg-white border-gray-200"
              } border rounded-2xl max-w-md w-full p-6 max-h-[90vh] overflow-y-auto`}
            >
              <div className="flex items-center justify-between mb-6">
                <h2
                  className={`text-2xl font-bold ${
                    isDarkMode ? "text-white" : "text-gray-900"
                  }`}
                >
                  Add Song
                </h2>
                <button
                  onClick={() => setShowSongModal(false)}
                  className={`${
                    isDarkMode
                      ? "text-gray-400 hover:text-white"
                      : "text-gray-600 hover:text-gray-900"
                  } transition-colors`}
                >
                  <X size={24} />
                </button>
              </div>

              <form onSubmit={handleCreateSong} className="space-y-4">
                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDarkMode ? "text-gray-400" : "text-gray-600"
                    }`}
                  >
                    Song Name *
                  </label>
                  <input
                    type="text"
                    value={songForm.songName || ""}
                    onChange={(e) =>
                      setSongForm({
                        ...songForm,
                        songName: e.target.value.slice(0, 100),
                      })
                    }
                    placeholder="Enter song name"
                    maxLength={100}
                    className={`w-full px-4 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:ring-orange-400 ${
                      isDarkMode
                        ? "bg-zinc-800 border-zinc-700 text-white"
                        : "bg-gray-100 border-gray-300 text-gray-900"
                    }`}
                  />
                  <p
                    className={`text-xs mt-1 text-right ${
                      isDarkMode ? "text-gray-500" : "text-gray-500"
                    }`}
                  >
                    {(songForm.songName || "").length}/100
                  </p>
                </div>

                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDarkMode ? "text-gray-400" : "text-gray-600"
                    }`}
                  >
                    Category *
                  </label>
                  <select
                    value={songForm.categoryId || ""}
                    onChange={(e) =>
                      setSongForm({ ...songForm, categoryId: e.target.value })
                    }
                    className={`w-full px-4 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:ring-orange-400 ${
                      isDarkMode
                        ? "bg-zinc-800 border-zinc-700 text-white"
                        : "bg-gray-100 border-gray-300 text-gray-900"
                    }`}
                  >
                    <option value="">Select a category</option>
                    {categories
                      .filter((c) => c.isActive && !c.isDeleted)
                      .map((cat) => (
                        <option key={cat._id} value={cat._id}>
                          {cat.categoryName || "Untitled"}
                        </option>
                      ))}
                  </select>
                </div>

                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDarkMode ? "text-gray-400" : "text-gray-600"
                    }`}
                  >
                    Upload Song File *
                  </label>
                  <div className="space-y-2">
                    <input
                      type="file"
                      accept="audio/*"
                      onChange={handleSongFileSelect}
                      disabled={uploadingFiles.song}
                      className="hidden"
                      id="song-file-input"
                    />
                    {!selectedSongFile ? (
                      <label
                        htmlFor="song-file-input"
                        className={`flex items-center justify-center gap-2 w-full px-4 py-3 rounded-lg border-2 border-dashed cursor-pointer transition-colors ${
                          uploadingFiles.song
                            ? "opacity-50 cursor-not-allowed"
                            : isDarkMode
                              ? "border-zinc-700 hover:border-orange-400 hover:bg-zinc-800"
                              : "border-gray-300 hover:border-orange-500 hover:bg-gray-100"
                        }`}
                      >
                        {uploadingFiles.song ? (
                          <>
                            <Loader
                              size={20}
                              className="animate-spin text-orange-400"
                            />
                            <span
                              className={
                                isDarkMode ? "text-gray-400" : "text-gray-600"
                              }
                            >
                              Uploading...
                            </span>
                          </>
                        ) : (
                          <>
                            <Upload size={20} className="text-orange-400" />
                            <span
                              className={
                                isDarkMode ? "text-gray-400" : "text-gray-600"
                              }
                            >
                              Click to select song file
                            </span>
                          </>
                        )}
                      </label>
                    ) : (
                      <div
                        className={`border-2 rounded-lg p-4 ${
                          isDarkMode
                            ? "border-green-500/30 bg-green-500/10"
                            : "border-green-200 bg-green-50"
                        }`}
                      >
                        <div className="flex items-center justify-between gap-2 mb-2">
                          <div className="flex items-center gap-2 flex-1 min-w-0">
                            <Check
                              size={20}
                              className="text-green-400 flex-shrink-0"
                            />
                            <p className="text-sm text-green-400 truncate">
                              File selected
                            </p>
                          </div>

                          <button
                            type="button"
                            onClick={() => {
                              setSelectedSongFile(null);
                              document.getElementById("song-file-input").value =
                                "";
                            }}
                            className="text-xs px-2 py-1 bg-orange-400 text-black rounded hover:bg-orange-500 transition-colors flex-shrink-0"
                          >
                            Change
                          </button>
                        </div>

                        <p className="text-xs text-gray-400 truncate">
                          {selectedSongFile.name}
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowSongModal(false)}
                    disabled={formSubmitting || uploadingFiles.song}
                    className={`flex-1 px-4 py-2.5 rounded-lg font-medium border transition-colors disabled:opacity-50 ${
                      isDarkMode
                        ? "border-zinc-700 text-white hover:bg-zinc-800"
                        : "border-gray-300 text-gray-900 hover:bg-gray-100"
                    }`}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={
                      formSubmitting ||
                      uploadingFiles.song ||
                      !(songForm.songName || "").trim() ||
                      !(songForm.categoryId || "").trim() ||
                      !selectedSongFile
                    }
                    className="flex-1 px-4 py-2.5 bg-orange-400 text-black font-semibold rounded-lg hover:bg-orange-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {formSubmitting ? (
                      <>
                        <RefreshCw size={16} className="animate-spin" />
                        Adding...
                      </>
                    ) : (
                      <>
                        <Plus size={16} />
                        Add Song
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {showViewModal && selectedItem && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div
              className={`${
                isDarkMode
                  ? "bg-black border-zinc-800"
                  : "bg-white border-gray-200"
              } border rounded-2xl max-w-lg w-full p-6`}
            >
              <div className="flex items-center justify-between mb-6">
                <h2
                  className={`text-2xl font-bold ${
                    isDarkMode ? "text-white" : "text-gray-900"
                  }`}
                >
                  Song Details
                </h2>
                <button
                  onClick={() => {
                    setShowViewModal(false);
                    setSelectedItem(null);
                  }}
                  className={`${
                    isDarkMode
                      ? "text-gray-400 hover:text-white"
                      : "text-gray-600 hover:text-gray-900"
                  } transition-colors`}
                >
                  <X size={24} />
                </button>
              </div>

              <div className="space-y-6">
                <div className="flex justify-center">
                  <div className="w-48 h-48 rounded-xl bg-orange-400 flex items-center justify-center shadow-lg">
                    <Music size={96} className="text-black" />
                  </div>
                </div>

                <div>
                  <h3
                    className={`text-sm font-medium mb-1 ${
                      isDarkMode ? "text-gray-400" : "text-gray-600"
                    }`}
                  >
                    Song Name
                  </h3>
                  <p
                    className={`text-lg font-semibold ${
                      isDarkMode ? "text-white" : "text-gray-900"
                    }`}
                  >
                    {selectedItem.songName || "N/A"}
                  </p>
                </div>

                <div>
                  <h3
                    className={`text-sm font-medium mb-1 ${
                      isDarkMode ? "text-gray-400" : "text-gray-600"
                    }`}
                  >
                    Category
                  </h3>
                  <p className={isDarkMode ? "text-white" : "text-gray-900"}>
                    {selectedItem.categoryId?.categoryName || "N/A"}
                  </p>
                </div>

                <div>
                  <h3
                    className={`text-sm font-medium mb-1 ${
                      isDarkMode ? "text-gray-400" : "text-gray-600"
                    }`}
                  >
                    Song URL
                  </h3>
                  <a
                    href={selectedItem.songUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-orange-400 hover:text-orange-500 break-all text-sm"
                  >
                    {selectedItem.songUrl}
                  </a>
                </div>

                <div>
                  <h3
                    className={`text-sm font-medium mb-1 ${
                      isDarkMode ? "text-gray-400" : "text-gray-600"
                    }`}
                  >
                    Audio Player
                  </h3>
                  <audio
                    controls
                    className="w-full mt-2"
                    src={selectedItem.songUrl}
                  >
                    Your browser does not support the audio element.
                  </audio>
                </div>

              </div>
            </div>
          </div>
        )}

        {confirmModal.show && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div
              className={`${
                isDarkMode
                  ? "bg-black border-zinc-800"
                  : "bg-white border-gray-200"
              } border rounded-2xl max-w-md w-full p-6`}
            >
              <div className="flex items-center justify-between mb-6">
                <h2
                  className={`text-2xl font-bold ${
                    isDarkMode ? "text-white" : "text-gray-900"
                  }`}
                >
                  {confirmModal.title}
                </h2>
                <button
                  onClick={() =>
                    setConfirmModal({
                      show: false,
                      title: "",
                      message: "",
                      onConfirm: null,
                      itemId: null,
                    })
                  }
                  className={`${
                    isDarkMode
                      ? "text-gray-400 hover:text-white"
                      : "text-gray-600 hover:text-gray-900"
                  } transition-colors`}
                >
                  <X size={24} />
                </button>
              </div>

              <p
                className={`mb-6 ${
                  isDarkMode ? "text-gray-400" : "text-gray-600"
                }`}
              >
                {confirmModal.message}
              </p>

              <div className="flex gap-3">
                <button
                  onClick={() =>
                    setConfirmModal({
                      show: false,
                      title: "",
                      message: "",
                      onConfirm: null,
                      itemId: null,
                    })
                  }
                  className={`flex-1 px-4 py-2.5 rounded-lg font-medium border transition-colors ${
                    isDarkMode
                      ? "border-zinc-700 text-white hover:bg-zinc-800"
                      : "border-gray-300 text-gray-900 hover:bg-gray-100"
                  }`}
                >
                  Cancel
                </button>
                <button
                  onClick={confirmModal.onConfirm}
                  className="flex-1 px-4 py-2.5 bg-red-500 text-white font-semibold rounded-lg hover:bg-red-600 transition-colors"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MusicManagement;
